import Image from "next/image";
import Link from "next/link";
import { styled } from "@mui/material/styles";
import AspectRatioContainer from "@common/AspectRatioContainer";
import Swimlane, { LeftArrow, RightArrow } from "@pageContent/common/Swimlane";
import { SCALE_ANIMATION, firstChildSelector, lastChildSelector, lastDirectChildDiv } from "@constants/consts";
import { colors, fonts } from "@constants/cssVariables";
import trackContentClick from "@utils/common/trackContentClick";

const PortraitCollectionContainer = styled("div")(({ theme }) => ({
  margin: theme.spacing(8, 0),

  [theme.breakpoints.up("md")]: {
    margin: theme.spacing(10, 0),
  },
}));

const PortraitCollectionHeader = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
}));

const PortraitCollectionTitle = styled("p")(() => ({
  font: fonts.header3,
  color: colors.neutral90,
}));

const PortraitCollectionShowAll = styled("div")(({ theme }) => ({
  font: fonts.buttonMedium,
  color: colors.primary,
}));

const Portrait = styled("div")(({ theme }) => ({
  position: "relative",
  marginRight: theme.spacing(2),
  width: "calc((100% - 101px) / 2)",
  maxWidth: 160,

  [firstChildSelector]: {
    marginLeft: theme.spacing(4),
  },

  [lastChildSelector]: {
    "&:after": {
      content: `""`,
      position: "absolute",
      width: 16,
      height: "100%",
      top: 0,
      right: -16,
    },
  },

  [theme.breakpoints.up("md")]: {
    width: "calc((100% - 208px) / 4)",
    maxWidth: 175,
    marginRight: theme.spacing(4),

    [firstChildSelector]: {
      marginLeft: theme.spacing(20),
    },

    [lastChildSelector]: {
      "&:after": {
        width: 80,
        right: -80,
      },
    },
  },

  [theme.breakpoints.up("lg")]: {
    width: "calc((100% - 239px) / 6)",
    maxWidth: 200,
  },
}));

const AspectRatio = styled(AspectRatioContainer)(({ theme }) => ({
  borderRadius: 8,
  [theme.breakpoints.up("md")]: {
    ...SCALE_ANIMATION,
  },
}));

const PortraitImage = styled(Image)(() => ({
  objectFit: "cover",
  borderRadius: 8,
}));

const StyledSwimlane = styled(Swimlane)(({ theme }) => ({
  [lastDirectChildDiv]: {
    display: "-webkit-box",
    padding: theme.spacing(4, 0),
  },

  [theme.breakpoints.up("md")]: {
    margin: theme.spacing(0, -20),

    [lastDirectChildDiv]: {
      padding: theme.spacing(6, 0),
    },
  },

  [`${LeftArrow}, ${RightArrow}`]: {
    top: "50%",
    transform: "translateY(-26px)",
    backgroundColor: colors.primary,
    color: colors.neutral0,

    "&:hover": {
      backgroundColor: colors.primary50,
      color: colors.neutral0,
    },

    "&:active": {
      backgroundColor: colors.primary120,
      color: colors.neutral0,
    },
  },
}));

const PortraitCollection: React.FC<LinkList> = ({ title, items, showAllText, showAllUrl }) => (
  <PortraitCollectionContainer>
    <PortraitCollectionHeader>
      <PortraitCollectionTitle>{title}</PortraitCollectionTitle>
      {showAllUrl && (
        <PortraitCollectionShowAll>
          <Link href={showAllUrl}>{showAllText}</Link>
        </PortraitCollectionShowAll>
      )}
    </PortraitCollectionHeader>
    <StyledSwimlane scrollToItemIndex={0} hasHoverEffect>
      {items.map((item, index) => (
        <Portrait
          key={item.id}
          onClick={() =>
            trackContentClick({
              position: index + 1,
              label: title,
              ...item,
            })
          }
          data-testid="portrait"
        >
          <Link target={item.target} href={item.url}>
            <AspectRatio aspectRatio="2:3">
              <PortraitImage
                alt={item.title}
                src={item.imageUrl}
                fill
                sizes="(max-width: 900px) 50vw, (max-width: 1200px) 30vw, 20vw"
              />
            </AspectRatio>
          </Link>
        </Portrait>
      ))}
    </StyledSwimlane>
  </PortraitCollectionContainer>
);

export default PortraitCollection;
